import { Injectable } from '@angular/core';
import { IPropertyFieldConfig } from '../../../shared/model/property-field-config.interface';
import {
  flattenObject,
  inferFormatter,
  pathToExportLabel,
  pathToLabel,
} from '../../utils/property-object-flattener';
import { FIELD_OVERRIDES, HIDDEN_FIELDS } from '../../config/property-field-overrides.config';

@Injectable({ providedIn: 'root' })
export class PropertyFieldsResolver {
  /**
   * Generates the list of resolved fields for any API response object.
   *
   * Process:
   * 1. Flattens the object recursively → { 'roof_condition.value': 4, ... }
   * 2. Filters out paths present in HIDDEN_FIELDS
   * 3. For each remaining field:
   *    a. Label:          override.label          ?? pathToLabel(path)
   *    b. Formatter:      override.formatter       ?? inferFormatter(convention) ?? null
   *    c. Tooltip:        override.tooltip         ?? null
   *    d. unitsDependent: override.unitsDependent  ?? false
   *
   * @param obj - API response object (IPropertyResponse, IDamageAssessmentResponse, etc.)
   */
  resolve(obj: any): IPropertyFieldConfig[] {
    const flat = flattenObject(obj);

    return Object.entries(flat)
      .filter(([path]) => !HIDDEN_FIELDS.has(path))
      .map(([path, value]) => {
        const override = FIELD_OVERRIDES[path] ?? {};
        return {
          path,
          value,
          label: override.label ?? pathToLabel(path),
          formatter: override.formatter ?? inferFormatter(path, flat),
          tooltip: override.tooltip ?? null,
          unitsDependent: override.unitsDependent ?? false,
        };
      });
  }

  /**
   * Generates a flat export record for any API response object.
   * No hardcoded fields — all are included automatically via flattenObject.
   *
   * @param obj    - API response object
   * @param prefix - Key prefix (e.g. 'Primary', 'Additional 1')
   */
  toExportRecord(obj: any, prefix = ''): Record<string, any> {
    const flat = flattenObject(obj);

    return Object.entries(flat)
      .filter(([path]) => !HIDDEN_FIELDS.has(path))
      .reduce<Record<string, any>>((acc, [path, value]) => {
        const label = FIELD_OVERRIDES[path]?.label ?? pathToExportLabel(path);
        const key = prefix ? `${prefix} ${label}` : label;
        acc[key] = value ?? null;
        return acc;
      }, {});
  }
}
